<template>
    <div class="content-wrapper">
        <!-- Page Loader -->
        <div class="vld-parent">
            <loading :active.sync="isLoading" :is-full-page="fullPage"></loading>
        </div>

        <breadcrumb :page="$t('form.company.create')"></breadcrumb>

        <div class="content">
            <div class="container-fluid">

                <b-alert v-model="alert" :variant="alertClass" dismissible fade>{{alertMessage}}</b-alert>

                <div class="card">
                    <form role="form" @submit.prevent="saveCompany()">
                        <div class="card-header bg-light">
                            <div class="card-title">{{$t('form.company.create')}}</div>
                            <button class="btn btn-primary float-right">{{$t('button.save')}}</button>
                        </div>

                        <div class="card-body">
                            <div class="form-group row">
                                <label class="col-sm-3" for="CompanyName">{{$t('form.company.companyName')}}
                                    <span class="req text-danger">*</span>
                                </label>
                                <div class="col-sm-9">
                                    <input id="CompanyName" type="text" v-model.trim="$v.company.CompanyName.$model"
                                        name="CompanyName" class="form-control"
                                        :class="{ 'is-invalid': $v.company.CompanyName.$error }">
                                    <div class="invalid-feedback" v-if="!$v.company.CompanyName.required">
                                        {{$t('form.validation.required')}}</div>
                                    <div class="invalid-feedback" v-if="!$v.company.CompanyName.maxLength">
                                        {{$t('form.validation.maxLength', {num: $v.company.CompanyName.$params.maxLength.max})}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Department">
                                    {{$t('form.company.department')}}
                                    <span class="text-danger">*</span>
                                </label>
                                <div class="col-sm-9">
                                    <input type="text" id="Department" v-model.trim="$v.company.Department.$model"
                                        class="form-control" :class="{ 'is-invalid': $v.company.Department.$error }">
                                    <div class="invalid-feedback" v-if="!$v.company.Department.required">
                                        {{$t('form.validation.required')}}</div>
                                    <div class="invalid-feedback" v-if="!$v.company.Department.maxLength">
                                        {{$t('form.validation.maxLength', {num: $v.company.Department.$params.maxLength.max})}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group brdbot">
                                <h5>
                                    <b>
                                        <u>{{$t('form.company.contactPerson')}}</u>
                                    </b>
                                </h5>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Title">{{$t('form.company.title')}}</label>
                                <div class="col-sm-9">
                                    <v-select :options="titles" v-model="company.ContactPersonTitle"
                                        :reduce="titles => titles"></v-select>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="FirstName">{{$t('form.company.firstName')}}
                                    <span class="req text-danger">*</span>
                                </label>
                                <div class="col-sm-9">
                                    <input type="text" id="ContactPersonFirstNameField"
                                        v-model.trim="$v.company.ContactPersonFirstName.$model" class="form-control"
                                        :class="{ 'is-invalid': $v.company.ContactPersonFirstName.$error }">
                                    <div class="invalid-feedback" v-if="!$v.company.ContactPersonFirstName.required">
                                        {{$t('form.validation.required')}}
                                    </div>
                                    <div class="invalid-feedback" v-if="!$v.company.ContactPersonFirstName.maxLength">
                                        {{$t('form.validation.maxLength', {num: $v.company.ContactPersonFirstName.$params.maxLength.max})}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="LastName">{{$t('form.company.lastName')}}
                                    <span class="req text-danger">*</span>
                                </label>
                                <div class="col-sm-9">
                                    <input type="text" id="ContactPersonLastNameField"
                                        v-model.trim="$v.company.ContactPersonLastName.$model" class="form-control"
                                        :class="{ 'is-invalid': $v.company.ContactPersonLastName.$error }">
                                    <div class="invalid-feedback" v-if="!$v.company.ContactPersonLastName.required">
                                        {{$t('form.validation.required')}}
                                    </div>
                                    <div class="invalid-feedback" v-if="!$v.company.ContactPersonLastName.maxLength">
                                        {{$t('form.validation.maxLength', {num: $v.company.ContactPersonLastName.$params.maxLength.max})}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="StreetAddress">{{$t('form.company.streetAddress')}}
                                    <span class="req text-danger">*</span>
                                </label>
                                <div class="col-sm-9">
                                    <input type="text" id="StreetAddressField"
                                        v-model.trim="$v.company.StreetAddress.$model" class="form-control"
                                        :class="{ 'is-invalid': $v.company.StreetAddress.$error }">
                                    <div class="invalid-feedback" v-if="!$v.company.StreetAddress.required">
                                        {{$t('form.validation.required')}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="City">{{$t('form.company.city')}}
                                    <span class="req text-danger">*</span>
                                </label>
                                <div class="col-sm-9">
                                    <input type="text" id="CityField" v-model.trim="$v.company.City.$model"
                                        class="form-control" :class="{ 'is-invalid': $v.company.City.$error }">
                                    <div class="invalid-feedback" v-if="!$v.company.City.required">
                                        {{$t('form.validation.required')}}</div>
                                    <div class="invalid-feedback" v-if="!$v.company.City.maxLength">
                                        {{$t('form.validation.maxLength', {num: $v.company.City.$params.maxLength.max})}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Zip">{{$t('form.company.zip')}}
                                    <span class="req text-danger">*</span>
                                </label>
                                <div class="col-sm-9">
                                    <input type="text" id="ZipField" v-model.trim="$v.company.Zip.$model"
                                        class="form-control" :class="{ 'is-invalid': $v.company.Zip.$error }">
                                    <div class="invalid-feedback" v-if="!$v.company.Zip.required">
                                        {{$t('form.validation.required')}}</div>
                                    <div class="invalid-feedback" v-if="!$v.company.Zip.maxLength">
                                        {{$t('form.validation.maxLength', {num: $v.company.Zip.$params.maxLength.max})}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Country">{{$t('form.company.country')}} <span
                                        class="req text-danger">*</span> </label>
                                <div class="col-sm-9">
                                    <v-select label="printable_name" :options="countries"
                                        v-model.trim="$v.company.Country.$model"
                                        :reduce="countries => countries.printable_name">
                                    </v-select>
                                    <div class="invalid-feedback custom-validate" v-if="invalidCountry">
                                        {{$t('form.validation.required')}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Phone">{{$t('form.company.phone')}}
                                    <span class="req text-danger">*</span>
                                </label>
                                <div class="col-sm-9">
                                    <input type="text" id="PhoneField" v-model.trim="$v.company.Phone.$model"
                                        class="form-control" :class="{ 'is-invalid': $v.company.Phone.$error }">
                                    <div class="invalid-feedback" v-if="!$v.company.Phone.required">
                                        {{$t('form.validation.required')}}</div>
                                    <div class="invalid-feedback" v-if="!$v.company.Phone.maxLength">
                                        {{$t('form.validation.maxLength', {num: $v.company.Phone.$params.maxLength.max})}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Mobile">{{$t('form.company.mobile')}}
                                    <span class="req text-danger">*</span>
                                </label>
                                <div class="col-sm-9">
                                    <input type="text" id="MobileField" v-model.trim="$v.company.Mobile.$model"
                                        class="form-control" :class="{ 'is-invalid': $v.company.Mobile.$error }">
                                    <div class="invalid-feedback" v-if="!$v.company.Mobile.required">
                                        {{$t('form.validation.required')}}</div>
                                    <div class="invalid-feedback" v-if="!$v.company.Mobile.maxLength">
                                        {{$t('form.validation.maxLength', {num: $v.company.Mobile.$params.maxLength.max})}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Fax">{{$t('form.company.fax')}}</label>
                                <div class="col-sm-9">
                                    <input id="Fax" type="text" v-model="company.Fax" name="Fax"
                                        class="form-control k-textbox" data-role="text"
                                        data-parsley-errors-container="#errId13">
                                    <span id="errId13" class="error"></span>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Email">{{$t('form.company.email')}}
                                    <span class="req text-danger">*</span>
                                </label>
                                <div class="col-sm-9">
                                    <input type="email" id="EmailField" v-model.trim="$v.company.Email.$model"
                                        class="form-control" :class="{ 'is-invalid': $v.company.Email.$error }">
                                    <div class="invalid-feedback" v-if="!$v.company.Email.required">
                                        {{$t('form.validation.required')}}</div>
                                    <div class="invalid-feedback" v-if="!$v.company.Email.email">
                                        {{$t('form.validation.validEmail')}}
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Email2">{{$t('form.company.email2')}}</label>
                                <div class="col-sm-9">
                                    <input id="Email2" type="text" v-model="company.Email2" name="Email2"
                                        class="form-control k-textbox" data-role="text"
                                        data-parsley-errors-container="#errId15">
                                    <span id="errId15" class="error"></span>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Email3">{{$t('form.company.email3')}}</label>
                                <div class="col-sm-9">
                                    <input id="Email3" type="text" v-model="company.Email3" name="Email3"
                                        class="form-control k-textbox" data-role="text"
                                        data-parsley-errors-container="#errId16">
                                    <span id="errId16" class="error"></span>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="WebAddress">{{$t('form.company.webAddress')}}</label>
                                <div class="col-sm-9">
                                    <input id="WebAddress" v-model="company.WebAddress" name="WebAddress" type="text"
                                        class="form-control k-textbox" data-role="text"
                                        data-parsley-errors-container="#errId17">
                                    <span id="errId17" class="error"></span>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3" for="Notes">{{$t('form.company.notes')}}</label>
                                <div class="col-sm-9">
                                    <textarea id="Notes" rows="5" v-model="company.Notes" name="Notes"
                                        class="form-control k-textbox" data-role="textarea"
                                        data-parsley-errors-container="#errId18"></textarea>
                                    <span id="errId18" class="error"></span>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import {
        required,
        minLength,
        maxLength,
        alpha,
        numeric,
        decimal,
        email
    } from "vuelidate/lib/validators";

    export default {
        props: ["permissions"],
        data() {
            return {

                alert: false,
                alertClass: '',
                alertMessage: '',

                isLoading: false,
                fullPage: true,

                invalidCountry: false,

                company: {
                    CompanyName: null,
                    Department: null,
                    ContactPersonTitle: null,
                    ContactPersonFirstName: null,
                    ContactPersonLastName: null,
                    StreetAddress: null,
                    City: null,
                    Zip: null,
                    Country: null,
                    Phone: null,
                    Mobile: null,
                    Fax: null,
                    Email: null,
                    Email2: null,
                    Email3: null,
                    WebAddress: null,
                    Notes: null
                },

                titles: ["Mr.", "Mrs.", "Sir", "Madam", "Ms", "Miss", "Dr."],
                countries: []
            };
        },
        
        validations: {
            company: {
                CompanyName: {
                    required,
                    maxLength: maxLength(100)
                },
                Department: {
                    required,
                    maxLength: maxLength(100)
                },
                ContactPersonFirstName: {
                    required,
                    maxLength: maxLength(100)
                },
                ContactPersonLastName: {
                    required,
                    maxLength: maxLength(100)
                },
                StreetAddress: {
                    required,
                    maxLength: maxLength(255)
                },
                City: {
                    required,
                    maxLength: maxLength(100)
                },
                Country: {
                    required
                },
                Zip: {
                    required,
                    maxLength: maxLength(50)
                },
                Phone: {
                    required,
                    maxLength: maxLength(100)
                },
                Mobile: {
                    required,
                    maxLength: maxLength(100)
                },
                Email: {
                    required,
                    email
                }
            }
        },

        mounted() {
            if (!this.permissions.createCompanies) {
                return this.$router.push("/");
            }

            this.fetchCountries();
        },

        methods: {

            saveCompany() {
                this.$v.$touch();

              

               
                if (this.$v.$invalid) {
                     let errorMsg = '';
          
                      if(this.$v.company.CompanyName.$error){
                            errorMsg = 'Company Name';
                      }

                    if(this.$v.company.Department.$error){
                            errorMsg = errorMsg ? errorMsg +", Department": "Department";
                    }

                    if(this.$v.company.ContactPersonFirstName.$error){
                            errorMsg = errorMsg ? errorMsg +", Contact Person FirstName": "Contact Person FirstName";
                      }

                    if(this.$v.company.ContactPersonLastName.$error){
                            errorMsg = errorMsg ? errorMsg +", Contact Person LastName": "Contact Person LastName";
                      }

                    if(this.$v.company.StreetAddress.$error){
                            errorMsg = errorMsg ? errorMsg +", Street Address": "Street Address";
                      }

                    if(this.$v.company.City.$error){
                            errorMsg = errorMsg ? errorMsg +", City": "City";
                      }

                     if(this.$v.company.Country.$error){
                            errorMsg = errorMsg ? errorMsg +", Country": "Country";
                      }

                    if(this.$v.company.Zip.$error){
                            errorMsg = errorMsg ? errorMsg +", Zip": "Zip";
                      }

                      if(this.$v.company.Phone.$error){
                            errorMsg = errorMsg ? errorMsg +", Phone": "Phone";
                      }

                    if(this.$v.company.Mobile.$error){
                            errorMsg = errorMsg ? errorMsg +", Mobile": "Mobile";
                      }
                 
                    if(this.$v.company.Email.$error){
                            errorMsg = errorMsg ? errorMsg +", Email": "Email";
                      }

                    this.showAlert(this.$t('form.validation.mandatoryFieldsError', {errors:errorMsg}), 'danger');
                    return;
                }

                this.isLoading = true;

                axios
                    .post("/api/companies", this.company)
                    .then(response => {
                        this.isLoading = false;

                        if (response.data.success) {
                            this.showAlert(response.data.message);

                            setTimeout(() => {
                                this.$router.push("/company/list");
                            }, 2000);
                        } else if (response.data.error) {
                            this.showAlert(response.data.message, 'danger');
                        }
                    })
                    .catch(error => {
                        this.isLoading = false;
                        console.log("error");
                    });
            },

            fetchCountries() {
                axios
                    .get("/api/countries")
                    .then(({
                        data
                    }) => this.countries = data);
            },

            showAlert(msg, cls = 'success') {
                this.alertMessage = msg;
                this.alertClass = cls;
                this.alert = true;
            }
        }
    };

</script>

<style scoped>
    .custom-validate {
        display: block !important;
    }

</style>
